import { Service, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map, of, shareReplay, switchMap } from 'rxjs';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AuthService } from 'src/app/services/auth.service';
import { NexusQueries } from 'src/app/graphql/queries/nexus.queries';
import { GraphqlFunctionalGroup } from 'src/app/graphql/models/output/functional-group.output.model';
import { NexusUser } from 'src/app/models/nexus-user';
import { GraphqlNexusUser } from 'src/app/graphql/models/output/nexus-user.output.model';
import { GraphqlSerializedNexusUser } from 'src/app/graphql/models/output/serialized-nexus-user.output.model';
import { convertGraphqlFunctionalGroupToFunctionalGroup, convertGraphqlNexusUserToNexusUser } from 'src/app/graphql/mappers/nexus.mappers';

/**
 * Contains queries related to Nexus user information and groups.
 */
@Service()
export class NexusService {
	/** To request data from graphql server. */
	private apolloClientService = inject(ApolloClientService);

	/** To get current user. */
	private authService = inject(AuthService);

	/**
	 * Shared observable of all functional groups.
	 */
	public allFunctionalGroups$ = this.apolloClientService.abstractionLayerClient$.pipe(
		switchMap(client =>
			client.query<{ nexusAllFunctionalGroups: GraphqlFunctionalGroup[] }>({
				query: NexusQueries.ALL_FUNCTIONAL_GROUPS_QUERY,
			}),
		),
		map(result => {
			const data = result.data?.nexusAllFunctionalGroups ?? [];
			return data.map(item => convertGraphqlFunctionalGroupToFunctionalGroup(item));
		}),
		shareReplay(1),
	);

	/**
	 * Shared observable of all nexus users.
	 */
	public allNexusUsers$ = this.apolloClientService.abstractionLayerClient$.pipe(
		switchMap(client =>
			client.query<{ nexusAllUsers: GraphqlNexusUser[] }>({
				query: NexusQueries.NEXUS_ALL_USERS_QUERY,
			}),
		),
		map(result => {
			const data = result.data?.nexusAllUsers ?? [];
			return data.map(item => convertGraphqlNexusUserToNexusUser(item));
		}),
		shareReplay(1),
	);

	public currentNexusUser$ = this.authService.userData$.pipe(
		switchMap(userData => {
			if (!userData.isLoggedIn) {
				return of(null);
			}
			return this.getNexusUser$(userData.userName);
		}),
	);

	/**
	 * Gets a single nexus user by username.
	 * @param username Username of user to get.
	 * @returns Observable of user.
	 */
	public getNexusUser$(username: string): Observable<NexusUser | null> {
		return this.apolloClientService.abstractionLayerClient$.pipe(
			switchMap(client =>
				client.query<{ nexusUser: GraphqlSerializedNexusUser }>({
					query: NexusQueries.NEXUS_USER_QUERY,
					variables: { username: username },
				}),
			),
			map(result => {
				const data = result.data?.nexusUser;

				if (data && data.success) {
					const parsedData = JSON.parse(data.returnValue) as GraphqlNexusUser;
					return convertGraphqlNexusUserToNexusUser(parsedData);
				}
				return null;
			}),
		);
	}
}
